<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { Download, FileBarChart, Plus, Trash2 } from 'lucide-vue-next'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import RelativeTime from '@/components/RelativeTime.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useDialog } from '@/composables/useDialog'
import AppLayout from '@/layouts/AppLayout.vue'
import {
  apiReportExportPath,
  downloadApiReportExportPath,
  editReportPath,
  newReportPath,
  reportPath,
  reportsPath,
} from '@/routes'
import type { BreadcrumbItem } from '@/types'
import type { BaseEntityOption, ExportStatus, RecentExport, SavedReport } from '@/types/reports'

const props = defineProps<{
  saved_reports: SavedReport[]
  recent_exports: RecentExport[]
  base_entities: BaseEntityOption[]
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Reports', href: reportsPath() }]

const tableColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'base_entity', label: 'Base Entity' },
  { key: 'created_at', label: 'Created' },
  { key: 'actions', label: 'Actions', align: 'right' },
]

const exportColumns: TableColumn[] = [
  { key: 'base_entity', label: 'Base Entity' },
  { key: 'filename', label: 'Filename' },
  { key: 'status', label: 'Status' },
  { key: 'requested_at', label: 'Requested' },
  { key: 'actions', label: 'Actions', align: 'right' },
]

const EXPORT_STATUS_LABELS: Record<ExportStatus, string> = {
  pending: 'Pending',
  processing: 'Processing…',
  completed: 'Completed',
  failed: 'Failed',
}

const EXPORT_STATUS_VARIANTS: Record<ExportStatus, 'default' | 'secondary' | 'destructive'> = {
  pending: 'secondary',
  processing: 'secondary',
  completed: 'default',
  failed: 'destructive',
}

function entityLabel(key: string): string {
  return props.base_entities.find((e) => e.key === key)?.label ?? key
}

const { confirm } = useDialog()

async function confirmDelete(report: SavedReport) {
  const ok = await confirm({
    title: 'Delete report?',
    description: `Are you sure you want to delete "${report.name}"? This cannot be undone.`,
    confirmLabel: 'Delete',
    variant: 'destructive',
  })
  if (!ok) return
  router.delete(reportPath(report.slug), { preserveScroll: true })
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

async function confirmDeleteExport(exp: RecentExport) {
  const ok = await confirm({
    title: 'Delete export?',
    description: 'Are you sure you want to delete this export? This cannot be undone.',
    confirmLabel: 'Delete',
    variant: 'destructive',
  })
  if (!ok) return

  await fetch(apiReportExportPath(exp.id), {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken() },
  })
  router.reload({ only: ['recent_exports'] })
}
</script>

<template>
  <Head title="Reports" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <FileBarChart class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Reports</h1>
            <p class="text-muted-foreground text-sm">Build custom reports and export them to CSV</p>
          </div>
        </div>
        <Button as-child>
          <Link :href="newReportPath()">
            <Plus class="size-4" />
            New Report
          </Link>
        </Button>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <CardTitle class="text-base">Saved Reports</CardTitle>
        </CardHeader>
        <CardContent class="p-0">
          <div
            v-if="saved_reports.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <FileBarChart class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">No saved reports yet</p>
            <Button variant="outline" size="sm" as-child>
              <Link :href="newReportPath()">
                <Plus class="size-4" />
                Build your first report
              </Link>
            </Button>
          </div>

          <AppDataTable v-else :columns="tableColumns">
            <tr
              v-for="report in saved_reports"
              :key="report.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link :href="editReportPath(report.slug)" class="text-foreground hover:text-primary hover:underline">
                  {{ report.name }}
                </Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ entityLabel(report.base_entity) }}</td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="report.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDelete(report)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>
        </CardContent>
      </Card>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <CardTitle class="text-base">Recent Exports</CardTitle>
        </CardHeader>
        <CardContent class="p-0">
          <div
            v-if="recent_exports.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <Download class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">No exports yet — export a report to CSV to see it here</p>
          </div>

          <AppDataTable v-else :columns="exportColumns">
            <tr
              v-for="exp in recent_exports"
              :key="exp.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">{{ entityLabel(exp.base_entity) }}</td>
              <td class="text-muted-foreground px-6 py-4 font-mono text-xs">{{ exp.filename ?? '—' }}</td>
              <td class="px-6 py-4">
                <Badge :variant="EXPORT_STATUS_VARIANTS[exp.status]">{{ EXPORT_STATUS_LABELS[exp.status] }}</Badge>
                <span v-if="exp.status === 'failed' && exp.error_message" class="text-muted-foreground ml-2 text-xs">
                  {{ exp.error_message }}
                </span>
              </td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="exp.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button v-if="exp.status === 'completed'" variant="ghost" size="sm" as-child>
                    <a :href="downloadApiReportExportPath(exp.id)">
                      <Download class="size-4" />
                      Download
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDeleteExport(exp)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
